// bereichIcons.tsx — ein Icon je Katalog-Bereich der Verantwortlichkeiten.
//
// Schlichte Linien-SVGs im Refined-Classic-Stil (Strichstärke 1.7, abgerundet),
// wie kvpIcons.tsx / riskIcons.tsx. Rein dekorativ, keine Logik.
//
// Zugeordnet wird über den NORMALISIERTEN Bereichsnamen: der Katalog ist in den
// Einstellungen pflegbar, ein neuer oder umbenannter Bereich darf nie ins Leere
// laufen — dafür gibt es den Fallback.

import * as React from "react";

const s = (children: React.ReactNode, size = 16): React.ReactElement => (
    <svg
        width={size}
        height={size}
        viewBox="0 0 20 20"
        fill="none"
        stroke="currentColor"
        strokeWidth={1.7}
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden
        focusable="false"
        style={{ flexShrink: 0, display: "block" }}
    >
        {children}
    </svg>
);

/** Normalisiert einen Bereichsnamen für den Icon-Abgleich (Umlaute, „&", Leerraum). */
export const normBereich = (v: string): string =>
    (v || "")
        .toLowerCase()
        .normalize("NFD")
        .replace(/[̀-ͯ]/g, "")
        .replace(/&/g, " und ")
        .replace(/[^a-z]/g, "");

const ICONS: Record<string, React.ReactElement> = {
    // Finanzen — Münze mit €
    finanzen: s(
        <>
            <circle cx="10" cy="10" r="6.5" />
            <path d="M12 7.6a3 3 0 100 4.8" />
            <path d="M6.6 9.3h3.2M6.6 10.9h3.2" />
        </>,
    ),
    // Fuhrpark — PKW
    fuhrpark: s(
        <>
            <path d="M3 12.4h14M4.4 12.4l1.3-3.6a1.4 1.4 0 011.3-.9h6a1.4 1.4 0 011.3.9l1.3 3.6" />
            <path d="M3 12.4v2.2M17 12.4v2.2" />
            <circle cx="6.4" cy="12.4" r="1.2" />
            <circle cx="13.6" cy="12.4" r="1.2" />
        </>,
    ),
    // IT & Daten — Bildschirm mit Datenbank-Ringen
    itunddaten: s(
        <>
            <rect x="3" y="4" width="14" height="9.5" rx="1.4" />
            <path d="M7.5 16.5h5M10 13.5v3" />
            <ellipse cx="10" cy="7.2" rx="3" ry="1.2" />
            <path d="M7 7.2v2.6c0 .66 1.34 1.2 3 1.2s3-.54 3-1.2V7.2" />
        </>,
    ),
    // Qualitätsmanagement — Siegel mit Haken
    qualitaetsmanagement: s(
        <>
            <circle cx="10" cy="8.4" r="4.6" />
            <path d="M8.1 8.4l1.4 1.4 2.6-2.8" />
            <path d="M7.4 12.4L6.6 17l3.4-1.7L13.4 17l-.8-4.6" />
        </>,
    ),
    // Recht — Waage
    recht: s(
        <>
            <path d="M10 4v12M6.2 16.4h7.6" />
            <path d="M4 6.6h12" />
            <path d="M4 6.6L2.4 10.4a2 2 0 003.2 0L4 6.6z" />
            <path d="M16 6.6l-1.6 3.8a2 2 0 003.2 0L16 6.6z" />
        </>,
    ),
    // Sicherheit — Schild
    sicherheit: s(
        <>
            <path d="M10 3l5.2 2v4.4c0 3.3-2.1 6.2-5.2 7.6-3.1-1.4-5.2-4.3-5.2-7.6V5L10 3z" />
            <path d="M7.9 9.6l1.5 1.5 3-3.2" />
        </>,
    ),
    // Steuern — Beleg mit Prozent
    steuern: s(
        <>
            <path d="M5 3h10v14l-2-1.2-1.7 1.2L10 15.8 8.3 17 6.6 15.8 5 17V3z" />
            <path d="M8 11.6l4-4" />
            <circle cx="8.2" cy="7.8" r="0.9" />
            <circle cx="11.8" cy="11.4" r="0.9" />
        </>,
    ),
    // Transport & Logistik — LKW
    transportundlogistik: s(
        <>
            <path d="M2.6 5.6h8.2v7.6H2.6z" />
            <path d="M10.8 8.6h3l2.6 2.6v2h-5.6z" />
            <circle cx="6" cy="14.6" r="1.3" />
            <circle cx="13.6" cy="14.6" r="1.3" />
            <path d="M7.3 14.6h5" />
        </>,
    ),
    // Versicherungen — Regenschirm
    versicherungen: s(
        <>
            <path d="M3 10a7 7 0 0114 0z" />
            <path d="M10 10v4.8a1.8 1.8 0 01-3.6 0" />
            <path d="M10 3v-.6" />
        </>,
    ),
    // Geschäftsführer und Gesellschafter — Person mit Krone
    geschaftsfuhrerundgesellschafter: s(
        <>
            <circle cx="10" cy="8.6" r="2.6" />
            <path d="M5 16.4a5 5 0 0110 0" />
            <path d="M7.4 4.6l1.3 1.4 1.3-1.9 1.3 1.9 1.3-1.4" />
        </>,
    ),

    // ── Die Organ-Abschnitte (seit 08/2026) ──────────────────────────────────
    // Nur die Organrollen haben eine Kategorie als Überschrift; alle weiteren
    // Rollen stehen unter ihrer Zuständigkeit (Steuern, Fuhrpark …) und nutzen
    // die Icons oben.
    //
    // Unternehmensleitung — Person mit Krone (wie zuvor der GF-Abschnitt)
    unternehmensleitung: s(
        <>
            <circle cx="10" cy="8.6" r="2.6" />
            <path d="M5 16.4a5 5 0 0110 0" />
            <path d="M7.4 4.6l1.3 1.4 1.3-1.9 1.3 1.9 1.3-1.4" />
        </>,
    ),
    // Gesellschafter/Beteiligte — Kreisdiagramm mit herausgelöstem Anteil.
    // Zweimal hinterlegt: Der Abschnitt heißt „Gesellschafter/Beteiligte", die
    // Zeilen darunter tragen den Katalog-Bereich „Beteiligung".
    gesellschafterbeteiligte: s(
        <>
            <circle cx="9.4" cy="10.4" r="6" />
            <path d="M9.4 10.4V4.4" />
            <path d="M9.4 10.4l4.9 3.5" />
        </>,
    ),
    beteiligung: s(
        <>
            <circle cx="9.4" cy="10.4" r="6" />
            <path d="M9.4 10.4V4.4" />
            <path d="M9.4 10.4l4.9 3.5" />
        </>,
    ),
    // Vertretung — Unterschrift/Federstrich
    vertretung: s(
        <>
            <path d="M3.2 13.6c2.6 0 3.4-8 5.4-8 1.3 0 1.1 5.2 2.4 5.2 1 0 1.3-2.4 2.4-2.4 1.1 0 1.2 2.6 3.4 2.6" />
            <path d="M3.6 16.4h12.8" />
        </>,
    ),
    // Aufsicht — Auge
    aufsicht: s(
        <>
            <path d="M2.4 10c2-3.4 4.6-5.1 7.6-5.1S15.6 6.6 17.6 10c-2 3.4-4.6 5.1-7.6 5.1S4.4 13.4 2.4 10z" />
            <circle cx="10" cy="10" r="2.1" />
        </>,
    ),
};

/** Fallback für unbekannte / neu angelegte Bereiche — schlichter Anhänger. */
const FALLBACK = s(
    <>
        <path d="M10.6 3H16v5.4l-7.3 7.3a1.6 1.6 0 01-2.3 0l-3.1-3.1a1.6 1.6 0 010-2.3L10.6 3z" />
        <circle cx="13.2" cy="6.8" r="1" />
    </>,
);

/** Icon zum Bereich; unbekannte Bereiche bekommen den Fallback. */
export function bereichIcon(bereich: string): React.ReactElement {
    return ICONS[normBereich(bereich)] ?? FALLBACK;
}
